/**
 * LigneDeConversation — une conversation dans la liste (la pile du chatteur, les discussions) :
 * l'`Avatar` (avec sa présence et son réseau dans les coins, `AvatarDeConversation`), le nom (plus gras quand il y
 * a du non-lu) et son drapeau, des étiquettes, le temps écoulé à droite ; dessous l'aperçu du
 * dernier message (précédé de son statut quand c'est le nôtre, remplacé par « écrit… » quand le
 * fan tape, par « Brouillon » quand on n'a pas fini), une valeur (le montant dépensé, le nombre de
 * messages), et le compteur de non-lus ; un pied optionnel (le flow qui tourne).
 *
 * La ligne ouverte (`actif`) se voile à la marque. Le compteur de non-lus est un `Badge` à la
 * marque (gris quand la conversation est en sourdine), jamais un « 0 » : sans non-lu, rien.
 * `bloquee` grise la ligne mais le clic passe (l'app explique pourquoi). Le clic droit et l'appui
 * long ouvrent `menu`.
 *
 *   <ListeDeConversations ariaLabel="Ma pile">
 *     <LigneDeConversation nom="Max" avatar={{ src: fan.photo, graine: fan.id, enLigne: true, reseau: <LogoTelegram /> }} langue="en" ecoule="3 min" apercu="tu fais quoi ?" nonLus={2}
 *                          actif onClick={() => ouvrir(id)} />
 *   </ListeDeConversations>
 */
import { useRef, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from "react";

import { Badge } from "../Badge";
import { useMenu, type EntreeMenu } from "../Menu";
import { cn } from "../utils";
import { DrapeauDeLangue } from "./DrapeauDeLangue";
import { AvatarDeConversation, type AvatarDeConversationProps } from "./Presence";
import { StatutDuMessage, type StatutMessage } from "./StatutDuMessage";
import "./conversation.css";

export interface ListeDeConversationsProps {
  children: ReactNode;
  ariaLabel?: string;
  className?: string;
}

export function ListeDeConversations({ children, ariaLabel, className }: ListeDeConversationsProps) {
  return (
    <ul className={cn("flex min-w-0 flex-col gap-0.5 p-1", className)} aria-label={ariaLabel}>
      {children}
    </ul>
  );
}

export interface LigneDeConversationProps {
  nom: ReactNode;
  /** L'avatar : l'`Avatar` de la librairie, avec sa présence et son réseau dans les coins (44 px). */
  avatar?: AvatarDeConversationProps;
  langue?: string | null;
  pays?: string | null;
  /** Juste après le nom : l'étoile, le nom de la créatrice. */
  apresNom?: ReactNode;
  /** Des `Badge` (le mot du signal, le moment, le verrou). */
  etiquettes?: ReactNode;
  /** « 3 min », « 2 h » — déjà écrit. */
  ecoule?: ReactNode;
  /** L'instant, pour `<time dateTime>`. */
  dateTime?: string;
  /** Le dernier message, en texte (ou « Photo », « Vocal »). */
  apercu?: ReactNode;
  /** Le statut du dernier message quand c'est le nôtre. */
  statut?: StatutMessage;
  /** « écrit… » — remplace l'aperçu, en vert. */
  ecrit?: ReactNode;
  /** Le texte qu'on n'a pas envoyé : « Brouillon » devant, en ambre. */
  brouillon?: ReactNode;
  /** Défaut « Brouillon ». */
  libelleBrouillon?: string;
  /** Le nombre de non-lus. 0 ou absent : rien. */
  nonLus?: number | null;
  /** Conversation en sourdine : le compteur reste gris. */
  nonLusDiscrets?: boolean;
  /** À droite de l'aperçu : un montant, un nombre de messages. */
  valeur?: ReactNode;
  /** Une troisième ligne : le flow qui tourne. */
  pied?: ReactNode;
  /** La conversation ouverte. */
  actif?: boolean;
  /** Grisée (pas à toi, hors shift) — le clic passe quand même. */
  bloquee?: boolean;
  onClick?: () => void;
  href?: string;
  /** Avant le clic (préchargement). */
  onPointerDown?: (e: PointerEvent<HTMLElement>) => void;
  menu?: readonly EntreeMenu[] | (() => EntreeMenu[]);
  title?: string;
  className?: string;
}

export function LigneDeConversation({
  nom,
  avatar,
  langue,
  pays,
  apresNom,
  etiquettes,
  ecoule,
  dateTime,
  apercu,
  statut,
  ecrit,
  brouillon,
  libelleBrouillon = "Brouillon",
  nonLus,
  nonLusDiscrets = false,
  valeur,
  pied,
  actif = false,
  bloquee = false,
  onClick,
  href,
  onPointerDown,
  menu,
  title,
  className,
}: LigneDeConversationProps) {
  const { ouvrir, ouvrirSous, menu: menuOuvert } = useMenu();
  const ligne = useRef<HTMLElement | null>(null);
  const appui = useRef<number | null>(null);
  const nonLu = nonLus !== undefined && nonLus !== null && nonLus > 0;
  const entrees = (): EntreeMenu[] => (typeof menu === "function" ? menu() : [...(menu ?? [])]);

  const annulerAppui = () => {
    if (appui.current !== null) {
      window.clearTimeout(appui.current);
      appui.current = null;
    }
  };

  const contenu = (
    <>
      {avatar && <AvatarDeConversation nom={typeof nom === "string" ? nom : undefined} {...avatar} />}
      <span className="cv-ligne-texte">
        <span className="cv-ligne-haut">
          <span className="cv-ligne-nom">
            <span className="cv-ligne-nom-texte">{nom}</span>
            <DrapeauDeLangue langue={langue} pays={pays} taille={16} />
            {apresNom}
          </span>
          {etiquettes && <span className="cv-ligne-etiquettes">{etiquettes}</span>}
          {ecoule !== undefined && ecoule !== null && (
            <time className="cv-ligne-ecoule" dateTime={dateTime}>
              {ecoule}
            </time>
          )}
        </span>
        <span className="cv-ligne-bas">
          <span className="cv-ligne-apercu">
            {ecrit ? (
              <span className="cv-ligne-apercu-texte cv-ligne-ecrit">{ecrit}</span>
            ) : brouillon ? (
              <>
                <span className="cv-ligne-brouillon">{libelleBrouillon}</span>
                <span className="cv-ligne-apercu-texte">{brouillon}</span>
              </>
            ) : (
              <>
                {statut && <StatutDuMessage statut={statut} />}
                <span className="cv-ligne-apercu-texte">{apercu}</span>
              </>
            )}
          </span>
          {valeur !== undefined && valeur !== null && <span className="cv-ligne-valeur">{valeur}</span>}
          {nonLu && (
            <span className="inline-flex flex-none" data-testid="non-lus">
              <Badge ton={nonLusDiscrets ? "gris" : "marque"} size="md">
                {nonLus > 99 ? "99+" : nonLus}
              </Badge>
            </span>
          )}
        </span>
        {pied !== undefined && pied !== null && <span className="cv-ligne-pied">{pied}</span>}
      </span>
    </>
  );

  const commun = {
    className: cn("cv-ligne", className),
    "data-actif": actif || undefined,
    "data-non-lu": nonLu || undefined,
    "data-bloquee": bloquee || undefined,
    "data-cliquable": onClick || href ? "" : undefined,
    "aria-current": actif ? ("true" as const) : undefined,
    "aria-disabled": bloquee || undefined,
    title,
    onContextMenu: menu
      ? (e: MouseEvent) => {
          annulerAppui();
          ouvrir(e, entrees());
        }
      : undefined,
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      onPointerDown?.(e);
      if (!menu || e.pointerType !== "touch") return;
      annulerAppui();
      appui.current = window.setTimeout(() => {
        appui.current = null;
        if (ligne.current) ouvrirSous(ligne.current, entrees());
      }, 500);
    },
    onPointerUp: menu ? annulerAppui : undefined,
    onPointerCancel: menu ? annulerAppui : undefined,
    onPointerLeave: menu ? annulerAppui : undefined,
  };

  return (
    <li className="min-w-0 list-none">
      {href !== undefined ? (
        <a ref={(el) => void (ligne.current = el)} href={href} {...commun}>
          {contenu}
        </a>
      ) : onClick ? (
        <div
          ref={(el) => void (ligne.current = el)}
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          }}
          {...commun}
        >
          {contenu}
        </div>
      ) : (
        <div ref={(el) => void (ligne.current = el)} {...commun}>
          {contenu}
        </div>
      )}
      {menuOuvert}
    </li>
  );
}
